import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentPlansApi } from '../api/client';
import { useTranslation } from '../i18n/useTranslation';

export default function PaymentHistory() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, paid, partial
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadPlans();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentUser(data.data);
      }
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await paymentPlansApi.list({ limit: 100 });
      
      if (response.data.success) {
        setPlans(response.data.data);
      } else {
        setError(t('common.error'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = plans.filter(plan => {
    // Status filter
    if (filter !== 'all') {
      if (filter === 'pending' && plan.status !== 'pending') return false;
      if (filter === 'paid' && plan.status !== 'paid') return false;
      if (filter === 'partial' && plan.status !== 'partially_paid') return false;
    }
    
    // Search filter (order number, customer, payment ID)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesOrderNumber = plan.order_number?.toString().includes(search);
      const matchesOrderId = plan.order_id?.toString().includes(search);
      const matchesPlanId = plan.id?.toString().includes(search);
      
      if (!matchesOrderNumber && !matchesOrderId && !matchesPlanId) {
        return false;
      }
    }
    
    // Date range filter
    if (dateFrom) {
      const planDate = new Date(plan.created_at);
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (planDate < fromDate) return false;
    }
    
    if (dateTo) {
      const planDate = new Date(plan.created_at);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (planDate > toDate) return false;
    }
    
    // User filter (for admins)
    if (selectedUser !== 'all' && currentUser?.role === 'admin') {
      if (plan.salesman_id?.toString() !== selectedUser) return false;
    }
    
    return true;
  });

  // Extract unique users for filter dropdown (admins only)
  useEffect(() => {
    if (currentUser?.role === 'admin' && plans.length > 0) {
      const uniqueUsers = [...new Map(
        plans
          .filter(p => p.salesman_id)
          .map(p => [p.salesman_id, { id: p.salesman_id, name: p.salesman_name || p.salesman_username }])
      ).values()];
      setUsers(uniqueUsers);
    }
  }, [plans, currentUser]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'paid': t('statuses.paid'),
      'partial': t('statuses.partial'),
      'pending': t('statuses.pending'),
      'failed': t('statuses.failed'),
    };
    return statusMap[status] || status;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async (planId) => {
    try {
      setDeleting(true);
      setError('');
      
      const response = await paymentPlansApi.delete(planId);
      
      if (response.data.success) {
        // Remove from local state
        setPlans(plans.filter(p => p.id !== planId));
        setDeleteConfirm(null);
      } else {
        setError('Erro ao excluir plano de pagamento');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir plano de pagamento');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">Histórico de Planos de Pagamento</h1>
        <p className="mt-2 text-gray-400">
          Visualize e gerencie todos os planos de pagamento criados anteriormente
        </p>
      </div>

      {/* Search and Advanced Filters */}
      <div className="mb-6 bg-dark-700 shadow rounded-lg p-4 border border-dark-600">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Bar */}
          <div className="md:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-300 mb-1">
              Buscar
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                placeholder="Número do pedido, ID do plano..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-dark-500 rounded-md leading-5 bg-dark-600 text-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-300 mb-1">
              Data Inicial
            </label>
            <input
              type="date"
              id="dateFrom"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block w-full px-3 py-2 border border-dark-500 rounded-md shadow-sm bg-dark-600 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block text-sm font-medium text-gray-300 mb-1">
              Data Final
            </label>
            <input
              type="date"
              id="dateTo"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="block w-full px-3 py-2 border border-dark-500 rounded-md shadow-sm bg-dark-600 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>

        {/* User Filter (Admin Only) */}
        {currentUser?.role === 'admin' && users.length > 0 && (
          <div className="mt-4">
            <label htmlFor="userFilter" className="block text-sm font-medium text-gray-300 mb-1">
              Filtrar por Vendedor
            </label>
            <select
              id="userFilter"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="block w-full md:w-64 px-3 py-2 border border-dark-500 rounded-md shadow-sm bg-dark-600 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Todos os vendedores</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || `User ${user.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Clear Filters Button */}
        {(searchTerm || dateFrom || dateTo || selectedUser !== 'all') && (
          <div className="mt-4">
            <button
              onClick={() => {
                setSearchTerm('');
                setDateFrom('');
                setDateTo('');
                setSelectedUser('all');
              }}
              className="text-sm text-primary-500 hover:text-primary-400 font-medium"
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Status Filters */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary-500 text-dark-900'
              : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
          }`}
        >
          {t('paymentHistory.filters.all')}
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-primary-500 text-dark-900'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('paymentHistory.filters.pending')}
        </button>
        <button
          onClick={() => setFilter('partial')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'partial'
              ? 'bg-primary-500 text-dark-900'
              : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
          }`}
        >
          {t('paymentHistory.filters.partial')}
        </button>
        <button
          onClick={() => setFilter('paid')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'paid'
              ? 'bg-primary-500 text-dark-900'
              : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
          }`}
        >
          {t('paymentHistory.filters.paid')}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-900 bg-opacity-50 border border-red-700 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-400">Carregando...</p>
        </div>
      )}

      {/* Plans List */}
      {!loading && !error && filteredPlans.length === 0 && (
        <div className="text-center py-12 bg-dark-700 rounded-lg border border-dark-600">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">Nenhum plano encontrado</h3>
          <p className="mt-1 text-sm text-gray-400">
            {filter === 'all' 
              ? t('paymentHistory.empty.subtitle')
              : `${t('paymentHistory.filters.' + filter)} ${t('paymentHistory.empty.subtitleFiltered')}`}
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/orders')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-dark-900 bg-primary-500 hover:bg-primary-600"
            >
              {t('paymentHistory.empty.searchOrder')}
            </button>
          </div>
        </div>
      )}

      {/* Plans Table */}
      {!loading && filteredPlans.length > 0 && (
        <div className="bg-dark-700 shadow-md rounded-lg overflow-hidden border border-dark-600">
          <table className="min-w-full divide-y divide-dark-600">
            <thead className="bg-dark-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  PEDIDO
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  CLIENTE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  VENDEDOR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  VALOR TOTAL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  PARCELAS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  CRIADO
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                  AÇÕES
                </th>
              </tr>
            </thead>
            <tbody className="bg-dark-700 divide-y divide-dark-600">
              {filteredPlans.map((plan) => (
                <tr key={plan.id} className="hover:bg-dark-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">
                      #{plan.order_id}
                    </div>
                    <div className="text-sm text-gray-400">
                      Loja: {plan.store_id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">Pedido #{plan.order_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {plan.salesman_name || plan.salesman_username || 'N/A'}
                    </div>
                    {plan.salesman_username && (
                      <div className="text-xs text-gray-400">@{plan.salesman_username}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-primary-500">
                      {formatCurrency(plan.order_total)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {plan.paid_parts} / {plan.total_parts} paga
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(plan.status)}`}>
                      {getStatusText(plan.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {formatDate(plan.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => navigate(`/payment-plans/${plan.id}`)}
                        className="text-primary-500 hover:text-primary-400"
                      >
                        {t('paymentHistory.table.viewDetails')}
                      </button>
                      {(plan.status === 'pending' || plan.status === 'partial') && (
                        <button
                          onClick={() => navigate(`/payment-plans/${plan.id}/edit`)}
                          className="text-yellow-500 hover:text-yellow-400 font-medium"
                        >
                          Editar
                        </button>
                      )}
                      {currentUser?.role === 'admin' && (
                        <button
                          onClick={() => setDeleteConfirm(plan)}
                          className="text-red-600 hover:text-red-900 font-medium"
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirmar Exclusão
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Tem certeza que deseja excluir o plano de pagamento do pedido <strong>#{deleteConfirm.order_number}</strong>?
                <br />
                Esta ação não pode ser desfeita e todos os dados relacionados serão removidos.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Excluindo...
                    </>
                  ) : (
                    'Excluir'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
